import { List, useTable } from "@refinedev/antd";
import { Input, Segmented, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { Link, useNavigate, useSearchParams } from "react-router";

import { DATE_TIME_FORMAT, VERIFICATION_STATUS } from "../labels";
import type { TutorListItem } from "../types";

const STATUS_OPTIONS = [
  { value: "PENDING", label: "İncelemede" },
  { value: "VERIFIED", label: "Onaylı" },
  { value: "REJECTED", label: "Reddedildi" },
  { value: "SUSPENDED", label: "Pasif" },
  // Approved tutors without a profile video (filter `video=missing`, 2026-10-13).
  { value: "VIDEO_MISSING", label: "Profil videosu eksik" },
  { value: "UNVERIFIED", label: "Başvuruyu dolduruyor" },
  { value: "", label: "Tümü" },
];

function filterValue(filters: ReturnType<typeof useTable>["filters"], field: string) {
  const filter = filters.find((item) => "field" in item && item.field === field);
  return (filter?.value as string | undefined) ?? "";
}

/** /tutors: applications waiting for review first, oldest first (permission `tutors.view`). */
export function TutorsPage() {
  const navigate = useNavigate();
  // The dashboard links here with `?video=missing`.
  const [params] = useSearchParams();
  const { tableProps, filters, setFilters } = useTable<TutorListItem>({
    resource: "tutors",
    pagination: { mode: "server", pageSize: 25 },
    filters: {
      initial:
        params.get("video") === "missing"
          ? [{ field: "video", operator: "eq", value: "missing" }]
          : [{ field: "status", operator: "eq", value: "PENDING" }],
    },
    syncWithLocation: true,
  });
  const status =
    filterValue(filters, "video") === "missing" ? "VIDEO_MISSING" : filterValue(filters, "status");
  const search = filterValue(filters, "search");

  const setFilter = (field: string, value: string) => {
    const nextStatus = field === "status" ? value : status;
    setFilters(
      [
        nextStatus === "VIDEO_MISSING"
          ? { field: "video", operator: "eq", value: "missing" }
          : { field: "status", operator: "eq", value: nextStatus },
        { field: "search", operator: "eq", value: field === "search" ? value : search },
      ],
      "replace",
    );
  };

  return (
    <List title="Eğitmenler">
      <Space wrap style={{ marginBottom: 16 }}>
        <Segmented
          options={STATUS_OPTIONS}
          value={status}
          onChange={(value) => setFilter("status", String(value))}
        />
        <Input.Search
          allowClear
          placeholder="Ad, soyad veya e-posta"
          defaultValue={search}
          onSearch={(value) => setFilter("search", value)}
          style={{ width: 260 }}
        />
      </Space>
      <Table
        {...tableProps}
        rowKey="id"
        scroll={{ x: "max-content" }}
        // The whole row opens the card, not only the name.
        onRow={(tutor) => ({
          onClick: () => navigate(`/tutors/${tutor.id}`),
          style: { cursor: "pointer" },
        })}
      >
        <Table.Column<TutorListItem>
          title="Eğitmen"
          dataIndex="name"
          render={(name: string, tutor) => (
            <Space direction="vertical" size={0}>
              <Link to={`/tutors/${tutor.id}`}>
                <Typography.Text strong>{name}</Typography.Text>
              </Link>
              <Typography.Text type="secondary">{tutor.email}</Typography.Text>
            </Space>
          )}
        />
        <Table.Column<TutorListItem>
          title="Eğitim"
          dataIndex="education"
          render={(education: TutorListItem["education"]) =>
            education ? `${education.university} · ${education.department}` : "—"
          }
        />
        <Table.Column<TutorListItem>
          title="Dersler"
          dataIndex="subjects"
          render={(subjects: string[]) =>
            subjects.map((subject) => <Tag key={subject}>{subject}</Tag>)
          }
        />
        <Table.Column<TutorListItem>
          title="Başvuru"
          dataIndex="onboardingSubmittedAt"
          render={(value: string | null) => (value ? dayjs(value).format(DATE_TIME_FORMAT) : "—")}
        />
        <Table.Column<TutorListItem>
          title="Durum"
          dataIndex="verificationStatus"
          render={(value: string) => (
            <Tag color={VERIFICATION_STATUS[value]?.color}>{VERIFICATION_STATUS[value]?.label}</Tag>
          )}
        />
      </Table>
    </List>
  );
}
